import {announce} from '@github-ui/aria-live'
import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useEffect, useRef, useState} from 'react'
import {copyRichText} from '../helpers/copy-rich-text'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {setSanitizedInnerHTML} from '@github-ui/sanitize/set-inner-html'

const copyConfirmationMsDelay = 2000

function createLinkHtml(href: string, titleHtml: SafeHTMLString, number: number) {
  const link = document.createElement('a')
  link.setAttribute('href', href)
  setSanitizedInnerHTML(link, titleHtml)
  link.append(` #${number}`)

  const container = document.createElement('div')
  container.appendChild(link)
  return container.innerHTML
}

export function PullRequestCopyLinkButton({
  owner,
  repository,
  number,
  titleHtml,
}: {
  owner: string
  repository: string
  number: number
  titleHtml: SafeHTMLString
}) {
  const [copied, setCopied] = useState(false)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    return () => clearTimeout(timeoutIdRef.current)
  }, [])

  const handleCopy = () => {
    const url = new URL(`/${owner}/${repository}/pull/${number}`, window.location.origin).href
    const html = createLinkHtml(url, titleHtml, number)

    setCopied(true)
    void copyRichText({html, plainText: url})
    announce('Copied!')
    clearTimeout(timeoutIdRef.current)
    timeoutIdRef.current = setTimeout(() => setCopied(false), copyConfirmationMsDelay)
  }

  return (
    <IconButton
      aria-label="Copy link"
      className={copied ? 'color-fg-success' : undefined}
      icon={copied ? CheckIcon : CopyIcon}
      onClick={handleCopy}
      size="small"
      variant="invisible"
    />
  )
}
